import React from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';

// The maplibre stylesheet moved into map_components/maplibre_csp so it ships
// in the lazy chunk with the library rather than in the render-blocking CSS.
// It is still bundled, not fetched from Mapbox's CDN: no third-party requests.

import { getEnv, isOnServer } from './helpers/env';
import { parseURL } from './helpers/links';
import StateLayer from './map_components/StateLayer';
import AlfehrestDate from './helpers/AlFehrestDate';
import IconLayer from './map_components/IconLayer';

const propTypes = {
  date: PropTypes.instanceOf(AlfehrestDate),
  locked: PropTypes.bool,
};
const defaultProps = {
  date: null,
  locked: false,
};

class Map extends React.Component {
  constructor(props) {
    super(props);
    this.layers = {
      event: null,
      monument: null,
      state: null,
      city: null,
    };

    this.state = {
      ready: false,
    };
    this.onMapLoaded = this.onMapLoaded.bind(this);
    this.onMapClicked = this.onMapClicked.bind(this);
  }

  componentDidMount() {
    if(isOnServer()) {
      return;
    }
    // Deliberately dynamic: this is what splits maplibre out of the entry
    // bundle (see map_components/maplibre_csp). It was already deferred to
    // componentDidMount, but a static require() is still bundled into main.js,
    // so the browser paid to download and parse ~950 KB before it could paint
    // anything. No webpackPreload here: the request would then fire whenever
    // main.js runs, including on the pages that render no map at all, and the
    // gap it would close is the few ms between main.js executing and this
    // componentDidMount.
    import(/* webpackChunkName: "maplibre" */ './map_components/maplibre_csp')
      .then(({ default: maplibregl }) => this.setupMap(maplibregl));
  }

  setupMap(maplibregl) {
    // The chunk can still be in flight when the component unmounts.
    if (this.unmounted) {
      return;
    }
    maplibregl.setWorkerUrl(getEnv("MAP_WORKER_URL"));

    // No access token: the basemap is our own, served by this app from
    // /map/<version>/ (see server/src/basemap.js).
    if(!window['rtl_plugin_loaded']) {
      // Self-hosted rather than fetched from Mapbox's CDN. Required for the
      // Arabic labels to shape and order correctly. Second argument defers the
      // download until a right-to-left label is actually encountered (the
      // callback parameter went away in maplibre-gl 4).
      maplibregl.setRTLTextPlugin(getEnv("MAP_RTL_PLUGIN_URL"), true);
      window['rtl_plugin_loaded'] = true;
    }
    // MapLibre resolves tile and glyph URLs inside a web worker, where a
    // root-relative path has no base to resolve against and the request fails.
    // Fetch the style here and make those URLs absolute, so the style file
    // itself stays origin-agnostic and works in dev, staging and production
    // without being rebuilt per environment.
    fetch(getEnv("MAP_STYLE_URL"))
      .then(response => response.json())
      .then((style) => {
        // Deliberately not `new URL()`: it percent-encodes the {z}/{x}/{y}
        // placeholders, which MapLibre then fails to substitute.
        const absolute = url =>
          /^https?:\/\//.test(url) ? url : window.location.origin + url;

        style.glyphs = absolute(style.glyphs);
        Object.keys(style.sources).forEach((key) => {
          const source = style.sources[key];
          if (source.tiles) {
            source.tiles = source.tiles.map(absolute);
          }
        });
        this.initMapWithStyle(maplibregl, style);
      });
  }

  initMapWithStyle(maplibregl, style) {
    // The component can unmount while the style request is still in flight.
    if (this.unmounted) {
      return;
    }
    this.map = new maplibregl.Map({
      container: 'map',
      style,
      zoom: 3,
      center: [45.0, 20.0],
      attributionControl: false,
      navigationControl: false,
      // The basemap stops at z6; keep users inside the range we ship tiles for
      // rather than letting them zoom into blank overscaled tiles.
      maxZoom: 7
    });
    this.map.on('load', this.onMapLoaded);
    this.map.on('click', this.onMapClicked);
    // No attribution control. Natural Earth is public domain and states that
    // "crediting the authors is unnecessary"; MapLibre GL JS is BSD-3, which
    // requires its notice in source distributions, not on screen. The OFL
    // fonts are covered by the licence files shipped beside the glyphs.
    this.map.addControl(new maplibregl.NavigationControl(), 'top-left');
  }

  componentWillUnmount() {
    if(isOnServer()) {
      return;
    }
    // The map is now created asynchronously, so it may not exist yet.
    this.unmounted = true;
    if(!this.map) {
      return;
    }
    this.map.off('load', this.onMapLoaded);
    this.map.off('click', this.onMapClicked);
  }

  onMapLoaded() {
    this.setState({ ready: true });
  }

  onMapClicked(ev) {
    const firstFeature = this.map.queryRenderedFeatures(ev.point)[0];
    const keys = ['icon', 'state']
    for (let i = 0; i < keys.length; i += 1) {
      // Did we get a click before layers are loaded?
      if(!this.layers[keys[i]]) {
        // Ignore it for now.
        continue;
      }
      const layers = this.layers[keys[i]].queryLayers();
      if (firstFeature && firstFeature.layer && layers.indexOf(firstFeature.layer.id) !== -1) {
        this.layers[keys[i]].onLayerClicked(ev, firstFeature);
        return;
      }
    }
  }

  toggleMapLock(state) {
    const handlers = [
      'dragRotate',
      'dragPan',
      'scrollZoom',
      'touchZoomRotate',
      'keyboard',
    ];
    if (!this.state.ready) {
      return;
    }
    const funcName = state ? 'disable' : 'enable';
    this.map.dragRotate[funcName]();
    this.map.dragPan[funcName]();
    this.map.scrollZoom[funcName]();
    this.map.touchZoomRotate[funcName]();
    this.map.keyboard[funcName]();
  }

  render() {
    let date = this.props.date || new AlfehrestDate(Number(this.props.match.params.date));
    const map = this.map;
    this.toggleMapLock(this.props.locked);
    const parts = parseURL(this.props.location.pathname);
    return (
      <div id="map">
        {this.state.ready &&
          map && (
            <div>
              <StateLayer
                ref={(c) => {
                  c && (this.layers.state = c);
                }}
                selectedEntity={
                  parts.entity && parts.entity === 'states' ? parts.id : null
                }
                offset={this.props.locked ? 0 : -100}
                map={map}
                date={date}
              />
              <IconLayer
                ref={(c) => {
                  c && (this.layers.icon = c);
                }}
                selectedEntity={parts}
                offset={this.props.locked ? 0 : -150}
                speed={this.props.locked ? 150 : 1.2}
                zoom={5}
                map={map}
                date={date}
              />
            </div>
          )}
      </div>
    );
  }
}

Map.defaultProps = defaultProps;
Map.propTypes = propTypes;

export default withRouter(Map);
